// import "pwa-mmt-ui-header/ssr/header.css";
import React, { useContext, useEffect, useState } from "react";

import { getAnalytics } from "@tm/insurance/analytics";
import {
  getSelectedCountriesFromLocalStorage,
  initializePageTracking,
  trackEvent,
  triggerPageExit,
} from "@tm/insurance/analytics/xdm";
import { pageStack, PAGE_NAME_CONSTANTS } from "@tm/insurance/navigation";
import {
  DesktopLayout,
  FormattedText,
  InsurancePlans,
  ListingEditDetails,
  ListingPageV2,
  ShowInsPlan,
} from "@tm/static";
import { usePlanType } from "@tm/ui";
import { ExperimentTrackingString, useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import ProductInfoSection from "../../../components/ProductInfoSection/ProductInfoSection";
import { convertDateToUnix, convertUnixToDate } from "../../../utils";
import { ListingPageProps } from "../types";

interface ITravelDate {
  startDate: Date;
  endDate: Date;
}

const analytics = getAnalytics();

const InsuranceListing = ({ listingData }: ListingPageProps) => {
  const {
    title,
    choosePlan,
    priceData,
    planData,
    cards,
    filterList = [],
    compareToast,
    comparePlansV2,
    showPlanData,
    travelDetails,
    chatBotInfo,
    productInfoBanner,
    disclaimer,
  } = listingData;

  const device = useBreakpointV2();

  const [country, setCountry] = useState<string | null>("");
  const [travelDate, setTravelDate] = useState<ITravelDate>();
  const [travellers, setTravellers] = useState<string | null>("");

  const [quickFilters, setQuickFilters] = useState<any>();

  const displayFilters = filterList[0];
  const experiment = useContext(ExperimentTrackingString) || "";
  const { type } = usePlanType();

  useEffect(() => {
    if (filterList) {
      const transformedFilters = displayFilters?.filters?.map(
        (filter, index) => ({
          ...filter,
          id: index.toString(),
          name: filter.title,
        })
      );
      setQuickFilters(transformedFilters);
    } else {
      const priceFilters = priceData?.map((filter) => {
        return {
          ...filter,
          isSelected: filter.name === "All" ? true : false,
        };
      });
      setQuickFilters(priceFilters);
    }
  }, []);

  useEffect(() => {
    pageStack.push(PAGE_NAME_CONSTANTS.LISTING);
    const queryParams = new URLSearchParams(window.location.search);

    const segment = queryParams.get("segment");
    const startDate = queryParams.get("startDate");
    const endDate = queryParams.get("endDate");
    const travellers = queryParams.get("travellers");
    const countries =
      travelDetails && travelDetails.countries
        ? travelDetails.countries
        : queryParams.get("countries");
    const subpageName = queryParams.get("type");

    countries && setCountry(countries.split(",").join(", "));
    setTravellers(travellers);

    let start, end;
    if (startDate && endDate) {
      start = convertUnixToDate(Number(startDate));
      end = convertUnixToDate(Number(endDate));

      setTravelDate({
        startDate: start,
        endDate: end,
      });
    }

    analytics?.trackPageLoadEvent(
      {
        pageName: "LISTING",
        country: countries || "",
        startDate: start?.toLocaleDateString("en-GB")?.replace(/\//gi, ":"),
        endDate: end?.toLocaleDateString("en-GB")?.replace(/\//gi, ":"),
        travellers: `${Number(travellers)}|0|0`,
        segment: segment || "",
        experiment,
      },
      type
    );
    let selectedCountries = [];
    if (countries) {
      selectedCountries = getSelectedCountriesFromLocalStorage(
        queryParams.get("countries")?.split(",") || []
      );
    }

    initializePageTracking(
      {
        page_context: {
          page_name: "listing",
          page_type: "funnel",
          funnel_step: "listing",
          ...(subpageName && { subpage_name: subpageName }),
        },
      },
      {
        countries: selectedCountries,
        journeyType: segment || "",
        travellers: travellers ? parseInt(travellers) : 1,
        fromDateTime: start ? convertDateToUnix(start) : 0,
        toDateTime: end ? convertDateToUnix(end) : 0,
      }
    );
    return () => {
      triggerPageExit();
    };
  }, []);

  const handleEducationModalShown = () => {
    analytics?.trackListingEvents?.(
      { actionType: "educationModal_shown" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_shown",
      },
    });
  };
  const handleEducationModalNextClick = () => {
    analytics?.trackListingEvents?.(
      { actionType: "educationModal_next_click" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_next_click",
      },
    });
  };
  const handleEducationModalSkipClick = () => {
    analytics?.trackListingEvents?.(
      { actionType: "educationModal_skip_click" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_skip_click",
      },
    });
  };
  const handleEducationModalPageShown = (pageId: string) => {
    analytics?.trackListingEvents?.(
      { actionType: `educationModal_${pageId}_shown` },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: `educationModal_${pageId}_shown`,
      },
    });
  };

  const handleProductInfoKnowMoreClick = () => {
    analytics?.trackListingEvents?.(
      { actionType: "productInfo_knowMore_clicked" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "productInfo_knowMore_clicked",
      },
    });
  };
  const handleProductInfoCardClick = () => {
    analytics?.trackListingEvents?.(
      { actionType: "productInfo_card_clicked" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "productInfo_card_clicked",
      },
    });
  };

  return (
    <React.Fragment>
      <DesktopLayout
        pageName="LISTING"
        chatbotBottomMargin={120}
        title={title}
        chatBotInfo={chatBotInfo}
        leftComp={
          <React.Fragment>
            <ListingEditDetails
              title={country}
              travelDates={travelDate}
              travellerCount={Number(travellers) || 1}
              showEditIcon={true}
            />
            {device == "lg" && showPlanData && (
              <ShowInsPlan data={showPlanData} />
            )}
            {device == "lg" && productInfoBanner && (
              <ProductInfoSection
                productInfoBanner={productInfoBanner}
                onEducationModalShown={handleEducationModalShown}
                onEducationModalNextClick={handleEducationModalNextClick}
                onEducationModalSkipClick={handleEducationModalSkipClick}
                onEducationModalPageShown={handleEducationModalPageShown}
                onKnowMoreClick={handleProductInfoKnowMoreClick}
                onProductCardClick={handleProductInfoCardClick}
              />
            )}
            {device == "lg" && disclaimer && (
              <FormattedText
                baseStyle={{
                  color: theme.greyText,
                  fontSize: "14px",
                  fontWeight: "400",
                }}
                value={disclaimer}
              />
            )}
            {/* TODO: we will integrate this once we add filters */}
            {/* {device == 'lg' && (
                            <ParaTag fontSize="md" color={theme.whiteText}>
                                <FormattedText value={listingInfo} />
                            </ParaTag>
                        )} */}

            {/* <ParaTag fontSize="md" color={theme.whiteText}>
                            <FormattedText value={listingInfo} />
                        </ParaTag> */}
          </React.Fragment>
        }
        rightComp={
          <React.Fragment>
            <InsurancePlans
              travelDetails={travelDetails}
              title={choosePlan?.planTitle}
              data={cards ? cards : planData}
              tagData={quickFilters ? quickFilters : priceData}
              quickFiltersTitle={`${displayFilters?.filterTitle || ""} ${
                displayFilters?.filterSubtitle || ""
              }`}
              quickFilters={quickFilters}
              setQuickFilters={setQuickFilters}
              compareToastData={compareToast}
              comparePlan={comparePlansV2}
              // TODO: we will integrate this once we add filters
              // sortnFilterTxt={
              //     listingMockData?.choosePlan?.sortnFilterTxt
              // }
            />

            {/* TODO: we will integrate this once we add filters */}
            {/* <NoInsurancePlan
                            title={constants.noInsTitle}
                            info={constants.noInsInfo}
                        /> */}
          </React.Fragment>
        }
        // bottomComp={ <LandingFooter
        //     playStoreImgURI={playStoreImg.src}
        //     appStoreImgURI={appStoreImg.src}
        //     facebookImgURI={facebookImg.src}
        //     linkedInImgURI={linkedInImg.src}
        //     twitterImgURI={twitterImg.src}
        //     footerData={footerData}
        // />}
      ></DesktopLayout>

      {/* NOT IN CURRENT SCOPE*/}
      {/* {device == 'sm' && (
                <FilterBottomComp
                    filtersList={listingMockData?.showPlanData}
                    handleFilterClick={() => {}}
                />
            )} */}
    </React.Fragment>
  );
};

export default InsuranceListing;
